/**
 * Records Page
 *
 * Dashboard showing all available entity types for the consumer.
 */

import ArrowIcon from '@mui/icons-material/ArrowForward';
import EntityIcon from '@mui/icons-material/Description';
import {
  Box,
  EmptyState,
  GlassButton,
  GlassCard,
  Grid,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { type EntityDefinition, useConsumerEntities } from '../../hooks/useEntities';

export default function RecordsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: entities, isLoading, error } = useConsumerEntities();

  if (error) {
    return (
      <PageTemplate variant="list" titleKey="navigation.records" icon={EntityIcon}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="error">{t('records.loadError')}</Typography>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate variant="list" titleKey="navigation.records" icon={EntityIcon}>
      <Grid container spacing={3}>
        {isLoading
          ? // Loading skeletons
            [1, 2, 3, 4].map((i) => (
              <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                <Box sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <ShimmerLoader variant="rectangular" height={180} />
                </Box>
              </Grid>
            ))
          : entities?.map((entity: EntityDefinition, index: number) => (
              <Grid key={entity.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <GlassCard
                  neonAccent="cyan"
                  animate
                  animationDelay={index * 0.1}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                  onClick={() => navigate(`/records/${entity.id}`)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      <EntityIcon sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {entity.pluralName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {entity.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 2,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      {t('records.viewManageEntity', { entity: entity.pluralName.toLowerCase() })}
                    </Typography>
                    <GlassButton size="small" endIcon={<ArrowIcon />}>
                      {t('common.view')}
                    </GlassButton>
                  </Box>
                </GlassCard>
              </Grid>
            ))}
      </Grid>

      {!isLoading && (!entities || entities.length === 0) && (
        <GlassCard neonAccent="green" animate>
          <EmptyState
            icon={<EntityIcon />}
            title={t('records.noRecordTypes')}
            description={t('records.noRecordTypesDescription')}
          />
        </GlassCard>
      )}
    </PageTemplate>
  );
}
